<template>
  <div class="caregiver-detail min-h-screen w-full bg-slate-50 flex flex-col text-base">
    <!-- 顶栏 -->
    <header
      class="sticky top-0 z-20 bg-white border-b border-slate-100"
      :style="{ paddingTop: 'env(safe-area-inset-top)' }"
    >
      <div class="flex items-center gap-3 px-4 py-3">
        <button
          type="button"
          class="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600"
          @click="$emit('back')"
        >
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 class="text-xl font-bold text-slate-900">任务列表</h1>
      </div>

      <!-- 进度条 -->
      <div class="px-4 pb-4">
        <div class="relative flex justify-between">
          <div class="absolute top-3 left-4 right-4 h-1 bg-slate-200 rounded-full overflow-hidden">
            <div
              class="h-full bg-family-blue transition-all duration-500 ease-out rounded-full"
              :style="{ width: progressWidth }"
            />
          </div>
          <div
            v-for="(step, idx) in progressSteps"
            :key="step.key"
            class="relative z-10 flex flex-col items-center flex-1 min-w-0"
          >
            <div
              class="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-500"
              :class="step.done || step.active ? 'bg-family-blue border-family-blue' : 'bg-white border-slate-300'"
            >
              <div
                v-if="step.done"
                class="w-2 h-2 rounded-full bg-white"
              />
              <div
                v-else-if="step.active"
                class="w-2.5 h-2.5 rounded-full bg-white animate-pulse"
              />
            </div>
            <span
              class="mt-1.5 text-[10px] sm:text-xs text-center leading-tight transition-colors duration-500 px-0.5"
              :class="step.active ? 'text-family-blue font-bold' : step.done ? 'text-family-blue' : 'text-slate-400'"
            >
              {{ step.label }}
            </span>
          </div>
        </div>
      </div>
    </header>

    <main class="flex-1 px-4 pb-6 overflow-y-auto">
      <!-- 状态 1：待出发 -->
      <section v-if="phase === 'departing'" class="space-y-4">
        <div class="bg-white rounded-2xl p-5 border border-dashed border-slate-300 shadow-sm relative">
          <span class="absolute -top-2 left-4 text-lg">📍</span>
          <div class="pt-2">
              <p class="text-sm text-slate-400">目的地</p>
              <p class="text-2xl font-bold text-slate-900 leading-tight mt-1">{{ task.hospital }}</p>
              <p class="text-lg text-slate-700 mt-2 leading-relaxed">{{ task.department }} · {{ task.bedNo }}</p>
            </div>
          <div class="border-t border-slate-100 pt-4 mt-4 flex items-center justify-between">
            <p class="text-lg font-bold text-slate-800">
              {{ task.patientName }} · {{ task.patientAge }}岁 · {{ task.patientGender }}
            </p>
            <span class="px-2.5 py-1 rounded-lg text-sm font-bold bg-red-50 text-red-500 border border-red-100">
              {{ task.careLevel }}
            </span>
          </div>
        </div>

        <div class="bg-blue-50 rounded-2xl p-5 border border-blue-100">
          <p class="text-sm text-blue-600 font-medium mb-2">病房位置（请牢记）</p>
          <p class="text-2xl font-bold text-slate-900 leading-snug">{{ task.wardLocation }}</p>
        </div>

        <a
          :href="'tel:' + task.familyPhone"
          class="flex items-center justify-center gap-3 w-full min-h-[56px] rounded-2xl bg-white border-2 border-family-blue text-family-blue text-lg font-bold shadow-sm active:scale-[0.98] transition-transform"
        >
          <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          联系家属 {{ task.familyPhoneMasked }}
        </a>
      </section>

      <!-- 状态 2：赶往中 -->
      <section v-else-if="phase === 'enroute'" class="space-y-4">
        <div class="flex items-center justify-between text-lg">
          <span class="font-bold text-slate-800">正在前往医院</span>
          <span class="text-family-blue font-bold text-base">
            {{ inGeofence ? "约 320m · 已进入围栏范围" : "约 1.2km · 未进入范围" }}
          </span>
        </div>

        <!-- 距离进度模拟 -->
        <div class="bg-white rounded-2xl p-4 shadow-sm">
          <div class="relative h-2 bg-slate-200 rounded-full overflow-hidden mb-3">
            <div
              class="absolute inset-y-0 left-0 bg-family-blue rounded-full transition-all duration-500"
              :style="{ width: inGeofence ? '85%' : '35%' }"
            />
          </div>
          <div class="flex justify-between text-xs text-slate-400">
            <span>出发</span>
            <span>1.5km</span>
            <span>1km</span>
            <span :class="inGeofence ? 'text-caregiver-green font-bold' : ''">500m</span>
            <span>到达</span>
          </div>
        </div>

        <div
          v-if="inGeofence"
          class="flex items-center gap-3 rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-4 animate-fade-in"
        >
          <div class="w-10 h-10 rounded-full bg-caregiver-green flex items-center justify-center text-white flex-shrink-0">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div>
            <p class="text-lg font-bold text-emerald-800">已进入医院 500m 范围</p>
            <p class="text-sm text-emerald-600 mt-0.5">到达打卡功能已解锁</p>
          </div>
        </div>

        <!-- 测试：模拟进入500米 -->
        <button
          type="button"
          class="w-full min-h-[48px] rounded-xl border-2 border-dashed border-amber-300 bg-amber-50 text-amber-700 text-base font-bold"
          @click="toggleGeofence"
        >
          {{ inGeofence ? "【测试】退出 500 米范围" : "【测试】模拟进入 500 米范围" }}
        </button>

        <div class="bg-white rounded-2xl p-5 border border-dashed border-slate-300 relative">
          <span class="absolute -top-2 left-4 text-lg">📍</span>
          <p class="text-sm text-slate-400 pt-1">目的地</p>
          <p class="text-2xl font-bold text-slate-900 mt-1">{{ task.hospital }}</p>
          <p class="text-lg text-slate-700 mt-2">{{ task.department }} · {{ task.bedNo }}</p>
          <div class="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
            <span class="text-lg font-bold text-slate-800">{{ task.patientName }} · {{ task.patientAge }}岁 · {{ task.patientGender }}</span>
            <span class="px-2 py-1 rounded text-sm font-bold bg-red-50 text-red-500">{{ task.careLevel }}</span>
          </div>
        </div>

        <input
          ref="cameraInput"
          type="file"
          accept="image/*"
          capture="environment"
          class="hidden"
          @change="onPhotoSelected"
        />
      </section>

      <!-- 状态 3：履约日志 -->
      <section v-else-if="phase === 'serving'" class="space-y-4">
        <div class="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0 flex-1">
              <div class="flex items-start justify-between gap-2">
                <p class="text-xl font-bold text-slate-900 leading-snug">
                  {{ task.patientName }} {{ task.patientAge }}岁 {{ task.patientGender }}
                </p>
                <a
                  :href="'tel:' + task.familyPhone"
                  class="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-50 text-family-blue text-sm font-bold"
                >
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  联系家属
                </a>
              </div>
              <p class="text-base text-slate-500 mt-2 leading-relaxed">
                {{ task.hospital }} · {{ task.department }} · {{ task.bedNo }}
              </p>
              <p class="text-sm text-slate-400 mt-1">{{ task.startDate }} ~ {{ task.endDate }}</p>
            </div>
          </div>
          <p class="text-right text-sm text-caregiver-green font-bold mt-2">已服务 {{ servedDays }} 天</p>
        </div>

        <div class="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div class="flex items-center justify-between mb-3">
            <span class="text-lg font-bold text-slate-800">服务进度</span>
            <span class="text-caregiver-green font-bold text-xl">{{ servedDays }}/{{ totalDays }}</span>
          </div>
          <div class="h-2.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              class="h-full bg-caregiver-green rounded-full transition-all duration-500"
              :style="{ width: totalDays ? (servedDays / totalDays * 100) + '%' : '0%' }"
            />
          </div>
        </div>

        <h3 class="text-lg font-bold text-slate-800">每日护理日志</h3>

        <article
          v-for="item in logList"
          :key="item.dateKey"
          class="rounded-2xl p-4 border transition-colors duration-300"
          :class="cardClass(item)"
        >
          <div class="flex items-center justify-between mb-3">
            <span class="text-lg font-bold text-slate-900">{{ item.displayDate }}</span>
            <span v-if="item.today" class="px-2.5 py-0.5 rounded-md text-sm font-bold bg-family-blue text-white">今天</span>
            <span v-else-if="item.missed && !expandedMissed[item.dateKey]" class="px-2.5 py-0.5 rounded-md text-sm font-bold bg-red-500 text-white">漏填 · 点击补填</span>
            <span v-else-if="item.submitted" class="text-sm text-slate-400">{{ formatTime(item.log.submittedAt) }} 已提交</span>
          </div>

          <!-- 漏填：空卡片 -->
          <div v-if="item.missed && !expandedMissed[item.dateKey]" class="py-6">
            <button
              type="button"
              class="w-full min-h-[80px] rounded-xl border-2 border-dashed border-red-200 bg-red-50/50 text-red-500 text-lg font-bold flex flex-col items-center justify-center gap-2"
              @click="expandMissed(item.dateKey)"
            >
              <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              点击补填今日日志
            </button>
          </div>

          <!-- 已提交只读展示 -->
          <div v-else-if="isReadOnlyLog(item)" class="space-y-3">
            <div class="flex flex-wrap gap-2">
              <span
                v-if="item.log.fed === true"
                class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-50 text-teal-700 text-base font-bold border border-teal-100"
              >
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>
                已喂食
              </span>
              <span
                v-if="item.log.turned === true"
                class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-50 text-teal-700 text-base font-bold border border-teal-100"
              >
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>
                已翻身
              </span>
            </div>
            <p class="text-base text-slate-600 flex items-center gap-2">
              <span class="w-2 h-2 rounded-full" :class="moodDot(item.log.mood)" />
              精神状态：{{ moodLabel(item.log.mood) }}
            </p>
            <p class="text-sm text-slate-500 leading-relaxed bg-slate-50 rounded-xl p-3">{{ logSummary(item) }}</p>
          </div>

          <!-- 可编辑（今天 / 补填中） -->
          <div v-else class="space-y-4">
            <div class="flex flex-wrap gap-2">
              <button
                type="button"
                class="min-h-[48px] px-5 rounded-lg text-base font-bold border-2 transition-all"
                :class="item.log.fed === true ? 'bg-teal-500 text-white border-teal-500' : 'bg-white text-slate-600 border-slate-200'"
                @click="setLog(item.dateKey, 'fed', true)"
              >已喂食</button>
              <button
                type="button"
                class="min-h-[48px] px-5 rounded-lg text-base font-bold border-2 transition-all"
                :class="item.log.fed === false ? 'bg-slate-400 text-white border-slate-400' : 'bg-white text-slate-600 border-slate-200'"
                @click="setLog(item.dateKey, 'fed', false)"
              >未喂食</button>
              <button
                type="button"
                class="min-h-[48px] px-5 rounded-lg text-base font-bold border-2 transition-all"
                :class="item.log.turned === true ? 'bg-teal-500 text-white border-teal-500' : 'bg-white text-slate-600 border-slate-200'"
                @click="setLog(item.dateKey, 'turned', true)"
              >已翻身</button>
              <button
                type="button"
                class="min-h-[48px] px-5 rounded-lg text-base font-bold border-2 transition-all"
                :class="item.log.turned === false ? 'bg-slate-300 text-white border-slate-300' : 'bg-white text-slate-600 border-slate-200'"
                @click="setLog(item.dateKey, 'turned', false)"
              >无需翻身</button>
            </div>
            <div>
              <p class="text-base font-bold text-slate-700 mb-2">精神状态</p>
              <div class="grid grid-cols-3 gap-2">
                <button
                  v-for="m in moods"
                  :key="m.value"
                  type="button"
                  class="min-h-[48px] rounded-lg text-base font-bold border-2 transition-all"
                  :class="item.log.mood === m.value ? m.activeClass : 'bg-white text-slate-600 border-slate-200'"
                  @click="setLog(item.dateKey, 'mood', m.value)"
                >{{ m.label }}</button>
              </div>
            </div>
            <p v-if="item.log.mood" class="text-sm text-slate-500 leading-relaxed bg-slate-50 rounded-xl p-3">{{ logSummary(item) }}</p>
          </div>
        </article>
      </section>
    </main>

    <!-- 底部大按钮区 -->
    <div
      class="sticky bottom-0 z-30 px-4 pt-3 bg-gradient-to-t from-slate-50 via-slate-50/95 to-transparent"
      :style="{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }"
    >
      <!-- 状态1：出发 -->
      <button
        v-if="phase === 'departing'"
        type="button"
        class="w-full min-h-[60px] rounded-2xl bg-orange-500 hover:bg-orange-600 text-white text-xl font-bold shadow-[0_8px_24px_rgb(249,115,22,0.4)] active:scale-[0.98] transition-all"
        @click="handleDepart"
      >
        开始（出发）
      </button>

      <!-- 状态2：到达打卡 -->
      <button
        v-else-if="phase === 'enroute'"
        type="button"
        class="w-full min-h-[72px] rounded-2xl text-white text-xl font-bold flex flex-col items-center justify-center gap-1 transition-all duration-300"
        :class="canArrive ? 'bg-caregiver-green shadow-[0_8px_24px_rgb(0,166,133,0.45)] arrive-pulse active:scale-[0.98]' : 'bg-slate-300 cursor-not-allowed'"
        :disabled="!canArrive"
        @click="triggerCamera"
      >
        <svg class="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span>我已到达 · 拍照打卡</span>
        <span class="text-sm font-normal opacity-90">实时拍照 · 禁止相册选择</span>
      </button>

      <!-- 状态3：完工 -->
      <button
        v-else-if="phase === 'serving' && !completed"
        type="button"
        class="w-full min-h-[64px] rounded-2xl bg-red-500 text-white text-xl font-bold shadow-[0_8px_24px_rgb(239,68,68,0.4)] active:scale-[0.98] transition-all flex flex-col items-center justify-center"
        @click="handleComplete"
      >
        <span class="flex items-center gap-2">
          <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          我已完工（订单结束）
        </span>
        <span class="text-sm font-normal mt-1 opacity-90">提交后将通知家属确认，请提醒线下结算</span>
      </button>
    </div>
  </div>
</template>

<script>
import { createTaskMachine, validateCameraFile } from "./caregiverTaskMachine";

export default {
  name: "CaregiverTaskDetail",
  props: {
    task: { type: Object, required: true },
  },
  data() {
    return {
      machine: null,
      expandedMissed: {},
      moods: [
        { label: "好", value: "good", activeClass: "bg-emerald-500 text-white border-emerald-500" },
        { label: "一般", value: "normal", activeClass: "bg-amber-500 text-white border-amber-500" },
        { label: "差", value: "bad", activeClass: "bg-red-500 text-white border-red-500" },
      ],
    };
  },
  computed: {
    phase() {
      return this.machine ? this.machine.getState().phase : "departing";
    },
    inGeofence() {
      return this.machine ? this.machine.getState().inGeofence : false;
    },
    completed() {
      return this.machine ? this.machine.getState().completed : false;
    },
    canArrive() {
      return this.machine ? this.machine.canArrive() : false;
    },
    progressSteps() {
      return this.machine ? this.machine.getProgress() : [];
    },
    progressWidth() {
      const idx = this.machine ? this.machine.activeStepIndex() : 0;
      const pct = (idx / (5 - 1)) * 100;
      return `${Math.min(100, pct)}%`;
    },
    logList() {
      return this.machine ? this.machine.getLogList() : [];
    },
    servedDays() {
      return this.machine ? this.machine.servedDays() : 0;
    },
    totalDays() {
      return this.machine ? this.machine.totalDays() : 0;
    },
  },
  created() {
    this.machine = createTaskMachine(this.task);
  },
  methods: {
    emitUpdate() {
      this.$emit("update", {
        id: this.task.id,
        machineState: this.machine.serialize(),
        status: this.completed ? "pending_confirm" : "serving",
      });
    },
    cardClass(item) {
      if (item.missed && !this.expandedMissed[item.dateKey]) return "bg-red-50/60 border-red-200 border-dashed";
      if (item.today) return "bg-white border-family-blue shadow-sm";
      return "bg-white border-slate-100 shadow-sm";
    },
    isReadOnlyLog(item) {
      if (item.today) return false;
      if (item.missed && !this.expandedMissed[item.dateKey]) return false;
      return item.submitted && item.log.fed !== null;
    },
    moodLabel(mood) {
      const map = { good: "良好", normal: "一般", bad: "较差" };
      return map[mood] || "未记录";
    },
    moodDot(mood) {
      if (mood === "good") return "bg-emerald-500";
      if (mood === "normal") return "bg-slate-400";
      if (mood === "bad") return "bg-red-400";
      return "bg-slate-300";
    },
    logSummary(item) {
      const log = item.log;
      const parts = [];
      if (log.fed === true) parts.push("三餐按时进食");
      if (log.fed === false) parts.push("进食较少");
      if (log.turned === true) parts.push("定时翻身");
      if (log.mood === "good") parts.push("精神状态良好");
      if (log.mood === "normal") parts.push("精神状态一般，略有咳嗽");
      if (log.mood === "bad") parts.push("精神状态较差，需密切关注");
      if (parts.length === 0) return "请完成今日护理记录";
      return parts.join("，") + "。";
    },
    formatTime(ts) {
      if (!ts) return "";
      const d = new Date(ts);
      return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    },
    expandMissed(dateKey) {
      this.$set(this.expandedMissed, dateKey, true);
    },
    handleDepart() {
      this.machine.startDepart();
      this.emitUpdate();
    },
    toggleGeofence() {
      const next = !this.machine.getState().inGeofence;
      this.machine.simulateGeofence(next);
      this.emitUpdate();
      this.$forceUpdate();
    },
    triggerCamera() {
      if (!this.canArrive) return;
      this.$refs.cameraInput.click();
    },
    onPhotoSelected(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      const check = validateCameraFile(file);
      if (!check.ok) {
        this.$message.warning(check.message);
        return;
      }
      this.machine.arriveWithPhoto(file);
      this.emitUpdate();
      this.$message.success("到达打卡成功！");
      this.$forceUpdate();
    },
    setLog(dateKey, field, value) {
      this.machine.updateLog(dateKey, { [field]: value });
      this.emitUpdate();
      this.$forceUpdate();
    },
    handleComplete() {
      this.machine.completeOrder();
      this.emitUpdate();
      this.$message.success("已提交完工申请，等待家属确认");
      setTimeout(() => this.$emit("back"), 1200);
    },
  },
};
</script>

<style scoped>
.caregiver-detail {
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Hiragino Sans GB",
    "Microsoft YaHei", sans-serif;
}

.arrive-pulse {
  animation: arrive-breathe 2s ease-in-out infinite;
}

@keyframes arrive-breathe {
  0%, 100% { transform: translateY(0); box-shadow: 0 8px 24px rgb(0 166 133 / 0.45); }
  50% { transform: translateY(-3px); box-shadow: 0 12px 32px rgb(0 166 133 / 0.55); }
}

.animate-fade-in {
  animation: fade-in 0.4s ease-out;
}

@keyframes fade-in {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
